import {CursorType, DiagramType} from '@/enums/diagrams';
import {Cursor} from './cursor';
import Highcharts from 'highcharts';
import {CursorData} from '../cursorDiagramState';
import {CursorLabelModel} from './labelModels/cursorLabelModel';
import {MarkerCursorLabelModel} from './labelModels/markerCursorLabelModel';
import {SimpleCursor} from '@/logic/cursors/simpleCursor';
import {MarkerCursor} from '@/logic/cursors/markerCursor';
import {SpectrumModulationCursor} from '@/logic/cursors/spectrumModulationCursor';
import {SignalModulationCursor} from '@/logic/cursors/signalModulationCursor';
import {HarmonicCursor} from '@/logic/cursors/harmonicCursor';
import {SubHarmonicCursor} from '@/logic/cursors/subHarmonicCursor';
import {HeatmapCursor} from '@/logic/cursors/heatmapCursor';
import {cursorsColorScheme} from '@/utils/colorSchemes';

/*
 Менеджер курсоров графика
*/

export class CursorManager {
  // Последний активный курсор
  lastActiveCursor: Cursor | undefined | null = null;

  // Список текущих активных курсоров
  private activeCursors: Set<Cursor> = new Set();

  // Показывать линию
  isShowLine = true;

  // Показывать отметки
  isShowTooltips = false;

  // График
  private chart: any = null;

  // Серия к которой привязываются курсоры
  bindedSeriesIds: string[] = [];

  // Словарь созданных объектов курсоров по их типу
  cursors: Cursor[] = [];

  private diagramType: DiagramType | null;
  private simpleLabelModel: CursorLabelModel | null;
  private markerLabelModel: MarkerCursorLabelModel | null;
  private applyCallback: Function | null = null;

  constructor(
    diagramType: DiagramType | null = null,
    simpleLabelModel: CursorLabelModel | null = null,
    markerLabelModel: MarkerCursorLabelModel | null = null,
    applyCallback: Function | null = null,
  ) {
    this.diagramType = diagramType;
    this.simpleLabelModel = simpleLabelModel;
    this.markerLabelModel = markerLabelModel;
    this.applyCallback = applyCallback;
  }

  // Окно информации о курсоре
  textBoxLabel: any = undefined;

  // Инициализация менеджера (передаем график и идентификаторы серий)
  init(chart: any, bindedSeriesIds: string[]) {
    this.chart = chart;
    this.bindedSeriesIds = bindedSeriesIds;
  }

  // Показывать линии курсоров
  showLine(show: boolean) {
    this.activeCursors.forEach(x => x.showLine(show));
    this.isShowLine = show;
  }

  // Показывать отметки курсоров
  showTooltips(show: boolean) {
    this.activeCursors.forEach(x => x.showTooltips(show));
    this.isShowTooltips = show;
  }

  /*
    Метод сбрасывает последний активный курсор
  */
  private clearLastActiveCursor() {
    const arr = [...this.activeCursors];
    const cursor = arr.pop();
    if (cursor) {
      cursor.clear();
      this.activeCursors.delete(cursor);
    }
    this.lastActiveCursor = arr.pop();
    this.lastActiveCursor?.freezeEvents(false);
  }

  /*
    Метод сбрасывает курсор
  */
  clearCursor(cursorType: CursorType) {
    this.cursors
      .filter(
        x =>
          x.type == cursorType ||
          (cursorType == CursorType.Cursor && x.type == CursorType.HarmonicCursor),
      )
      .forEach(cursor => {
        if (cursor.type === this.lastActiveCursor?.type) this.clearLastActiveCursor();
        else {
          this.activeCursors.delete(cursor);
          cursor.clear();
        }
      });
  }

  // Применить курсор
  applyCursor(cursorType: CursorType, cursorData: CursorData | null | undefined) {
    const c: Cursor[] = this.cursors.filter(
      x =>
        x.type == cursorType ||
        (cursorType == CursorType.Cursor && x.type == CursorType.HarmonicCursor),
    );
    if (!c.length) c.push(this.getNewCursor(cursorType, false));
    if (!this.bindedSeriesIds.length) return;
    c.forEach(cursor => {
      this.activeCursors.add(cursor);
      this.lastActiveCursor?.freezeEvents(true);
      this.lastActiveCursor = cursor;
      cursor.init(
        this,
        this.chart,
        this.bindedSeriesIds,
        this.isShowLine,
        this.isShowTooltips,
        cursorData,
      );
      cursor.update(this.isShowLine, this.isShowTooltips, cursorData, this.bindedSeriesIds);
    });
  }

  // Обновить курсор
  updateCursor(cursorType: CursorType, cursorData: CursorData | null | undefined) {
    let cursor: Cursor | null | undefined = cursorData
      ? this.cursors.find(x => x.id == cursorData.id)
      : null;
    if (!cursor) {
      cursor = this.cursors.find(
        x =>
          x.type == cursorType ||
          (cursorType == CursorType.Cursor && x.type == CursorType.HarmonicCursor),
      );
    }
    if (!cursor) {
      cursor = this.getNewCursor(cursorType);
    }
    if (!cursor || !this.bindedSeriesIds.length) return;
    cursor.update(this.isShowLine, this.isShowTooltips, cursorData, this.bindedSeriesIds);
  }

  // Обновить курсор
  updateCursorIdx(index: number, cursorData: CursorData | null | undefined) {
    let cursor: Cursor | null | undefined = cursorData
      ? this.cursors.find(x => x.id == cursorData.id)
      : null;
    if (!cursor && index < this.cursors.length) {
      cursor = this.cursors[index];
    }
    if (!cursor) {
      cursor = this.getNewCursor(cursorData?.type || CursorType.Cursor);
    }
    if (!cursor || !this.bindedSeriesIds.length) return;
    cursor.update(this.isShowLine, this.isShowTooltips, cursorData, this.bindedSeriesIds);
  }

  // Перерисовка активных курсоров (на update)
  updateActiveCursors(cursorDataMap: CursorData[], bindedSeriesIds: string[]) {
    this.bindedSeriesIds = bindedSeriesIds;
    this.activeCursors.forEach(x =>
      x.update(
        this.isShowLine,
        this.isShowTooltips,
        cursorDataMap.find(c => c.id == x.id),
        this.bindedSeriesIds,
      ),
    );
  }

  // Перерисовка активных курсоров (на render)
  renderActiveCursors() {
    this.activeCursors.forEach(x => x.render());
  }

  // Действия при изменении курсоров
  public onCursorChanged() {
    const labels: string[] = [];
    // TODO: переписать в функциональном стиле
    this.activeCursors.forEach(x => {
      if (x.isApplied()) labels.push(x.getLabelStr());
    });
    if (labels.length) this.showLabel(labels.join('| '));
    else this.clearLabel();
  }

  /*
    Отрисовать окно с текстом на графике
  */
  protected showLabel(str: string) {
    this.clearLabel();
    this.textBoxLabel = this.chart.renderer
      .label(str, 0, 2, 'rect', 0, 0, true)
      .attr({
        fill: 'rgba(255, 230, 176, 0.8)',
        stroke: 'black',
        'stroke-width': 1,
        padding: 2,
        paddingLeft: 5,
        paddingRight: 5,
        height: 16,
        r: 2,
        zIndex: 10000,
      })
      .add();
    this.textBoxLabel.align(
      Highcharts.extend(this.textBoxLabel.getBBox(), {
        align: 'center',
        x: 0,
        verticalAlign: 'top',
        y: 2,
      }),
      null,
      'spacingBox',
    );
  }

  /*
    Убрать окно с текстом
  */
  protected clearLabel() {
    if (this.textBoxLabel != undefined) {
      this.textBoxLabel.destroy();
      this.textBoxLabel = undefined;
    }
  }

  // Получить данные активных курсоров
  getActiveCursorsData() {
    const cursorData: CursorData[] = [];
    this.activeCursors.forEach(cursor => cursorData.push(cursor.getCursorData()));
    return cursorData;
  }

  getCursors() {
    return this.cursors;
  }

  /*  getCursors(cursorType: CursorType) {
    return this.cursors;
    if (cursorType == CursorType.Cursor)
      return this.cursors.filter(x => x.type == cursorType || x.type == CursorType.HarmonicCursor);
    else return this.cursors.filter(x => x.type == cursorType);
  } */

  getChart() {
    return this.chart;
  }

  private colorPickerIdx = 0;

  // Создать новый курсор
  getNewCursor(cursorType: CursorType, need_to_init = true) {
    let cursor!: Cursor;
    const color = cursorsColorScheme[this.colorPickerIdx];
    this.colorPickerIdx = (this.colorPickerIdx + 1) % cursorsColorScheme.length;
    if (cursorType == CursorType.MarkerCursor) {
      cursor = new MarkerCursor(this.markerLabelModel, this.applyCallback, color);
    } else if (cursorType == CursorType.ModulationCursor) {
      if (this.diagramType == DiagramType.Signal)
        cursor = new SignalModulationCursor(this.markerLabelModel, this.applyCallback, color);
      if (this.diagramType == DiagramType.Spectrum)
        cursor = new SpectrumModulationCursor(this.markerLabelModel, this.applyCallback, color);
    } else if (cursorType == CursorType.HarmonicCursor) {
      cursor = new HarmonicCursor(this.simpleLabelModel, this.applyCallback, color);
    } else if (cursorType == CursorType.SubHarmonicCursor) {
      cursor = new SubHarmonicCursor(this.simpleLabelModel, this.applyCallback, color);
    } else {
      if (this.diagramType == DiagramType.Spectrum)
        cursor = new HarmonicCursor(this.simpleLabelModel, this.applyCallback, color, 0);
      else if (this.diagramType == DiagramType.Signal)
        cursor = new SimpleCursor(this.simpleLabelModel, this.applyCallback, color);
      else cursor = new HeatmapCursor(this.simpleLabelModel, this.applyCallback, color);
    }
    this.cursors.push(cursor);
    this.activeCursors.add(cursor);
    if (need_to_init)
      cursor.init(
        this,
        this.chart,
        this.bindedSeriesIds,
        this.isShowLine,
        this.isShowTooltips,
        null,
      );
    return cursor;
  }

  removeCursor(cursor: Cursor) {
    // единственный курсор заданного типа не удаляем
    if (this.cursors.filter(x => x.type == cursor.type).length <= 1) return;
    this.activeCursors.delete(cursor);
    const idx = this.cursors.indexOf(cursor);
    if (idx > -1) {
      this.cursors.splice(idx, 1);
    }
    cursor.clear();
  }
}
